import { Button, Checkbox, FormControl, IconButton, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography, Grid, Box, Card, CardContent, Divider, Collapse } from '@mui/material';
import React, { ReactNode, useContext, useState } from 'react';
import AddIcon from '@mui/icons-material/AddCircle';
import InfoIcon from '@mui/icons-material/InfoRounded';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { PurchaseOrderDetailsContext, PurchaseOrderDetailsValues } from '../contexts/PurchaseOrderDetailsContext';
import { LineRecord } from '../interfaces/LineRecord';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { availablePurchaseOrderLineReasons, getReason } from '../interfaces/PurchaseOrderLineReason';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';

const TableCellSort = (props: { children: ReactNode, tableKey: keyof LineRecord }) => {
  const { order,
    orderBy,
    handleSort,
  } = useContext(PurchaseOrderDetailsContext) as PurchaseOrderDetailsValues;

  return <TableCell>
    <TableSortLabel active={orderBy === props.tableKey}
      hideSortIcon
      direction={order}
      onClick={() => handleSort(props.tableKey)}>
      {props.children}
    </TableSortLabel>
  </TableCell>;
};

const PurchaseOrdersDetailsTable = () => {
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const { handleNoteButtonClick,
    handleQuantityChange,
    handleUomsChange,
    handleReasonChange,
    sortedPoLines,
    isShowingTrafficCode,
    isReceiving,
    purchaseOrders,
    isEdited,
    isEditable,
    isCreatingReturnToVendor,
    handlePOLineFocus,
    disableUomLac,
    clearAll,
  } = useContext(PurchaseOrderDetailsContext) as PurchaseOrderDetailsValues;
  const { t } = useTranslation('purchaseOrderDetails');

  const toggleRowExpanded = (lineId: number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(lineId)) {
      newExpandedRows.delete(lineId);
    } else {
      newExpandedRows.add(lineId);
    }
    setExpandedRows(newExpandedRows);
  };
  const getColumnCount = () => {
    let count = 9;
    if (!isReceiving) count++;
    if (purchaseOrders?.showReason) count++;
    if (isShowingTrafficCode) count++;
    return count;
  };
  const renderBatchRows = (line: LineRecord) => {
    if (!line.batches || line.batches.length === 0 || !expandedRows.has(line.id)) {
      return null;
    }

    return (
      <TableRow>
        <TableCell
          colSpan={getColumnCount()}
          style={{ padding: 0 }}
        >
          <Collapse
            in={expandedRows.has(line.id)}
            timeout="auto"
            unmountOnExit
          >
            <Box sx={{
              backgroundColor: '#f8f9fa',
              mx: 2,
              my: 1,
            }}>
              <Box sx={{ p: 3 }}>
                <Grid
                  container
                  spacing={2}
                >
                  {line.batches.map((batch, index) => (
                    <Grid
                      item
                      xs={12}
                      key={batch.id || batch.tempId}
                    >
                      <Card
                        variant="outlined"
                        sx={{
                          position: 'relative',
                          p: 1,
                        }}
                      >
                        <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                          <Grid
                            container
                            spacing={3}
                            alignItems="flex-end"
                          >
                            <Grid
                              item
                              xs={12}
                              sm={6}
                              lg={3}
                            >
                              <TextField
                                size="small"
                                label={t('batchManagement.batchNumber')}
                                value={batch.batchNumber}
                                InputProps={{
                                  readOnly: true,
                                }}
                                fullWidth
                                variant="outlined"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#f5f5f5',
                                  },
                                }}
                              />
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              sm={6}
                              lg={3}
                            >
                              <TextField
                                size="small"
                                type="number"
                                label={t('batchManagement.batchQuantity')}
                                value={batch.batchQuantity}
                                InputProps={{
                                  readOnly: true,
                                }}
                                fullWidth
                                variant="outlined"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#f5f5f5',
                                  },
                                }}
                              />
                            </Grid>

                            <Grid
                              item
                              xs={12}
                              sm={6}
                              lg={4}
                            >
                              <TextField
                                size="small"
                                label={t('batchManagement.expirationDate')}
                                value={batch.expirationDate}
                                InputProps={{
                                  readOnly: true,
                                }}
                                fullWidth
                                variant="outlined"
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#f5f5f5',
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                      {index < (line.batches?.length || 0) - 1 && (
                        <Divider sx={{ my: 1, opacity: 0.3 }} />
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const renderBatchToggleButton = (line: LineRecord) => {
    if (!line.batches || line.batches.length === 0) {
      return null;
    }

    const isExpanded = expandedRows.has(line.id);

    return (
      <IconButton
        onClick={() => toggleRowExpanded(line.id)}
        size="small"
        sx={{
          transition: 'transform 0.2s',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
        }}
      >
        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    );
  };

  return <>
    <TableHeader>
      {'Items'}
      {sortedPoLines?.length && <Pill text={`${sortedPoLines?.length} Items`}></Pill>}
    </TableHeader>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {!isReceiving &&
              <TableCell>
                {t('selected')}
              </TableCell>}
            <TableCell >
            </TableCell>
            <TableCellSort tableKey='quantity'>
              {t(isCreatingReturnToVendor ? 'creditedQuantity' : 'qty')}
            </TableCellSort>
            {
              purchaseOrders?.showReason &&
              <TableCell>
                {t('reason')}
              </TableCell>
            }
            <TableCellSort tableKey='uom'>
              {t(isCreatingReturnToVendor ? 'creditedUom' : 'uom')}
            </TableCellSort>
            <TableCellSort tableKey='itemNumber'>
              {t('item')}
            </TableCellSort>
            <TableCellSort tableKey='itemDescription'>
              {t('itemDescription')}
            </TableCellSort>
            <TableCell>
              {t('orderedQuantity')}
            </TableCell>
            <TableCellSort tableKey='expectedQuantity'>
              {t(isCreatingReturnToVendor ? 'receivedQty' : 'shippedQty')}
            </TableCellSort>
            <TableCellSort tableKey='expectedUom'>
              {t(isCreatingReturnToVendor ? 'receivedUom' : 'shippedUom')}
            </TableCellSort>
            <TableCellSort tableKey='lineNumber'>
              {t('poLine')}
            </TableCellSort>
            {isShowingTrafficCode &&
              <TableCellSort tableKey='tariffCode'>
                {t('tariffCode')}
              </TableCellSort>}
            <TableCellSort tableKey='note'>
              {t('note')}
            </TableCellSort>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPoLines?.map((PoLine) =>
            <React.Fragment key={PoLine.id}>
              <TableRow
                hover
                onFocus={() => handlePOLineFocus(PoLine.id ?? 0)}
                onClick={PoLine.batches && PoLine.batches.length > 0 ? () => toggleRowExpanded(PoLine.id) : undefined}
                sx={{
                  cursor: PoLine.batches && PoLine.batches.length > 0 ? 'pointer' : 'default',
                }}
              >
                {!isReceiving &&
                  <TableCell>
                    <Checkbox
                      disabled
                      checked={isEdited(PoLine)}
                    />
                  </TableCell>}
                <TableCell>
                  <Button size='md'
                    sx={{ pr: '5px', pl: '5px' }}
                    variant='primary'
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(`/store/item/${PoLine.id}?sku=${PoLine.itemNumber}`);
                    }}>
                    View
                  </Button>
                </TableCell>
                <TableCell>
                  {(isEditable || isCreatingReturnToVendor) ? (
                    <FormControl fullWidth
                      sx={{ minWidth: '60px' }}>
                      <TextField
                        type='text'
                        inputProps={{ pattern: '[0-9]*' }}
                        onClick={($event) => $event.stopPropagation()}
                        value={PoLine.quantity}
                        onChange={handleQuantityChange(PoLine.id ?? -1)}
                      />
                    </FormControl>
                  ) : (
                    PoLine.quantity
                  )}
                </TableCell>
                {
                  purchaseOrders?.showReason &&
                  <TableCell>
                    {
                      PoLine.hasDiscrepancy && isEditable &&
                      <FormControl fullWidth>
                        <Select
                          value={PoLine.reasonForCreditRequest ?? ''}
                          onChange={($event) => handleReasonChange(PoLine.id, $event.target.value)}
                          onClick={($event) => $event.stopPropagation()}
                          displayEmpty
                          renderValue={(value) => {
                            const selectedItem = availablePurchaseOrderLineReasons.find(item => item.value === value);
                            return selectedItem ? t(getReason(selectedItem.key)) : '';
                          }}
                        >
                          {availablePurchaseOrderLineReasons.map((item) => (
                            <MenuItem key={item.key}
                              value={item.value}>
                              {t(getReason(item.key))}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    }
                    {
                      !isEditable &&
                      <Typography>{PoLine.reasonForCreditRequest}</Typography>
                    }
                  </TableCell>
                }

                <TableCell>
                  {!disableUomLac && (isEditable || isCreatingReturnToVendor) ?
                    <FormControl fullWidth>
                      <Select
                        value={PoLine.uom}
                        onChange={handleUomsChange(PoLine.id ?? -1)}
                        onClick={($event) => $event.stopPropagation()}
                        displayEmpty
                        renderValue={(value) => {
                          const selectedUom = PoLine.uomList?.find(uom => uom.code === value);
                          return selectedUom ? selectedUom.code : value;
                        }}
                      >
                        {PoLine.uomList?.map((uom) => (
                          <MenuItem key={uom.code}
                            value={uom.code}>
                            {uom.code} {uom.description && `(${uom.description})`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    :
                    PoLine.uom}
                </TableCell>
                <TableCell>
                  {PoLine.itemNumber}
                </TableCell>
                <TableCell>
                  {PoLine.itemDescription}
                </TableCell>
                <TableCell>
                  {PoLine.orderedQuantity}
                </TableCell>
                <TableCell>
                  {isCreatingReturnToVendor ? PoLine.originalQuantity : PoLine.expectedQuantity}
                </TableCell>
                <TableCell>
                  {isCreatingReturnToVendor ? PoLine.originalUom : PoLine.expectedUom}
                </TableCell>
                <TableCell>
                  {PoLine.lineNumber}
                </TableCell>
                {isShowingTrafficCode &&
                  <TableCell>
                    {PoLine.tariffCode}
                  </TableCell>}
                <TableCell>
                  {isEditable &&
                    <Tooltip title={PoLine.note}>
                      <IconButton onClick={($event) => { handleNoteButtonClick(PoLine.id, PoLine.note); $event.stopPropagation(); }}>
                        {PoLine.note != '' ? <EditNoteIcon /> : <AddIcon />}
                      </IconButton>
                    </Tooltip>}
                  {!isEditable && PoLine.note != '' &&
                    <Tooltip title={PoLine.note}>
                      <IconButton aria-label={PoLine.note}
                        onClick={($event) => { handleNoteButtonClick(PoLine.id, PoLine.note); $event.stopPropagation(); }}>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>}
                  {renderBatchToggleButton(PoLine)}
                </TableCell>
              </TableRow>
              {renderBatchRows(PoLine)}
            </React.Fragment>,
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </>;
};

export default PurchaseOrdersDetailsTable;